import { useUploadFile } from '@hooks/index';
import { siteWorkerService } from '@services/index';
import Typography from 'antd/lib/typography';
import { UploadFile } from 'antd/lib/upload/interface';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, FormikHelpers } from 'formik';
import { textColor, toSnakeCase } from '@helpers/index';
import { formatRut, normalizeRut } from 'chilean-rutify';
import {
  Alert,
  Col,
  message,
  Row,
  Space,
  Table,
} from 'antd';
import {
  Form,
  FormItem,
  Input,
  Select,
  SubmitButton,
} from 'formik-antd';
import {
  CheckCircleOutlined,
  CloseOutlined,
  PaperClipOutlined,
  StopOutlined,
} from '@ant-design/icons';
import Modal from '@components/molecules/modal';
import BasicButtonBG from '@components/atoms/basic-button-bg';
import InputTextField from '@components/atoms/input-text-field';
import Dragger from '@components/molecules/dragger';
import PermissionsGate from '@config/permissionGate';
import { SCOPES } from '@config/permissionsMap';
import { addAnnotationProps, AnnotationFormValues } from './types';

const AddAnnotationModal = ({
  worker,
  siteWorkerAnnotationFormValidation,
  siteWorkerAnnotationInitialValues,
  siteChilds,
  isSiteChildsLoading,
  countryConfig,
}: addAnnotationProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { Text } = Typography;
  const { t } = useTranslation();
  const handleCancel = () => setModalOpen(false);
  const showModal = () => {
    setFileList([]);
    setModalOpen(true);
  };
  const { uploadFile } = useUploadFile();
  const { useAddAnnotationMutation } = siteWorkerService;
  const [addAnnotation] = useAddAnnotationMutation();
  const buttonValidation = worker?.blocked;
  const siteColumns = [
    { title: t('site.code'), dataIndex: 'code', width: 120 },
    { title: t('site.name'), dataIndex: 'name' },
  ];

  const {
    rut: rutConfig,
  } = countryConfig?.fields || {};

  const onSubmit = async (
    values: AnnotationFormValues,
    { setSubmitting, resetForm }: FormikHelpers<AnnotationFormValues>,
  ) => {
    try {
      const formattedWorker = toSnakeCase({
        ...values,
        rut: normalizeRut(values.rut),
        selectedSites: values.selectedSites,
      }) as AnnotationFormValues;
      await addAnnotation(formattedWorker).unwrap();
      message.success(t('siteWorker.uploadAnnotationSuccess'), 2);
      setSubmitting(false);
      resetForm();
      handleCancel();
    } catch {
      message.error(t('siteWorker.uploadAnnotationError'), 2);
      setSubmitting(false);
    }
  };
  const mimeTypes = ['application/pdf'];
  const { Option } = Select;
  const { TextArea } = Input;
  const buttonTitle = worker?.blocked
    ? t('siteWorker.authorize')
    : t('siteWorker.block');
  const buttonColor = worker?.blocked ? 'lightBlue' : 'lightRed';
  const buttonIcon = worker?.blocked ? (
    <CheckCircleOutlined className={`${textColor.green} py-1`} />
  ) : (
    <StopOutlined className={`${textColor.red} py-1`} />
  );

  return (
    <Row>
      <Formik
        enableReinitialize
        initialValues={{
          ...siteWorkerAnnotationInitialValues,
          rut: formatRut(worker?.documentNumber ?? ''),
          selectedSites: [],
        }}
        validationSchema={siteWorkerAnnotationFormValidation}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue }) => {
          const isFormComplete = Boolean(values.rut)
            && Boolean(values.effectStr)
            && Boolean(values.comments)
            && Boolean(values.selectedSites?.length);

          return (
            <Modal
              title={(
                <Text className="text-lg text-blue-900">
                  {t('siteWorker.addAnnotation')}
                </Text>
              )}
              closeIcon={<CloseOutlined className="text-2xl" />}
              visible={modalOpen}
              onCancel={handleCancel}
              width={719}
              displayButtons={false}
              footer={(
                <Form>
                  <Row justify="end">
                    <BasicButtonBG
                      text={t('documentation.cancel')}
                      loading={false}
                      onClick={handleCancel}
                    />
                    <SubmitButton disabled={!isFormComplete}>
                      {t('documentation.save')}
                    </SubmitButton>
                  </Row>
                </Form>
              )}
            >
              <Form layout="vertical">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Space direction="vertical" className="w-full">
                      <Alert
                        type="warning"
                        description={t('siteWorker.annotationMessage')}
                        className="w-full"
                        showIcon
                      />
                    </Space>
                  </Col>
                  <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                    <InputTextField
                      name="rut"
                      size="middle"
                      label={t(rutConfig?.labelKey || 'siteWorker.rut')}
                      placeholder={rutConfig?.placeholder}
                      disabled={!!worker}
                    />
                  </Col>
                  <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                    <FormItem name="effectStr" label={t('siteWorker.action')}>
                      <Select name="effectStr">
                        <Option value="allowance">{t('siteWorker.authorize')}</Option>
                        <Option value="prohibition">{t('siteWorker.block')}</Option>
                      </Select>
                    </FormItem>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <FormItem name="comments" label={t('siteWorker.comment')}>
                      <TextArea name="comments" allowClear size="middle" rows={2} />
                    </FormItem>
                  </Col>
                  <PermissionsGate scopes={[SCOPES.canLinkSitesToAnnotation]}>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                      <Space direction="vertical" size="small" className="w-full">
                        <Alert
                          type="info"
                          description={t('site.annotationInfoMessage')}
                          className="bg-blue-100 w-full"
                          showIcon
                        />
                      </Space>
                      <FormItem name="selectedSites">
                        <Table
                          loading={isSiteChildsLoading}
                          className="py-2"
                          rowKey="id"
                          columns={siteColumns}
                          dataSource={siteChilds}
                          pagination={false}
                          size="small"
                          scroll={{ y: 200 }}
                          rowSelection={{
                            type: 'checkbox',
                            selectedRowKeys: values.selectedSites,
                            onChange: (keys) => setFieldValue('selectedSites', keys as number[]),
                          }}
                        />
                      </FormItem>
                    </Col>
                  </PermissionsGate>
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <FormItem name="uploadId" label="File">
                      <Dragger
                        fileList={fileList}
                        setFileList={setFileList}
                        uploadFile={uploadFile}
                        className="w-full avatar-uploader"
                        maxCount={1}
                        multiple={false}
                        mimeTypes={mimeTypes}
                      />
                    </FormItem>
                  </Col>
                </Row>
              </Form>
            </Modal>
          );
        }}
      </Formik>
      {!buttonValidation ? (
        <BasicButtonBG
          text={t('siteWorker.addAnnotation')}
          icon={<PaperClipOutlined className="text-xs xs:text-xs sm:text-xs md:text-xs lg:text-xs xl:text-sm 2xl:text-sm" />}
          type="primary"
          loading={false}
          onClick={showModal}
          size="middle"
          buttonColorType="blue"
        />
      ) : (
        <BasicButtonBG
          text={(
            <Text className={`${textColor.darkBlue} py-0.5 text-sm xs:text-xs sm:text-xs md:text-xs lg:text-xs xl:text-xs 2xl:text-xs`}>
              {buttonTitle}
            </Text>
          )}
          icon={buttonIcon}
          type="link"
          loading={false}
          onClick={showModal}
          size="middle"
          buttonColorType={buttonColor as 'lightRed' | 'lightBlue'}
        />
      )}
    </Row>
  );
};

AddAnnotationModal.defaultProps = {
  worker: null,
};
export default AddAnnotationModal;
